import { Component, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { EventsService, UserEvent, ParticipantUser, EventAccess, EventAdmin, OrganizerDetails } from '../../services/events.service';
import { UserService } from '../../services/user.service';
import { ModalComponent, ModalButton } from '../../common-components/modal/modal.component';
import { PageHeaderComponent, PageHeaderButton, User, getLocaleFromLang, formatDate, SvgWrapperComponent, isEventStarted, isEventCompleted, isEventActive } from 'quest-atlas-angular-components';
import { PageFooterComponent } from '../../common-components/page-footer/page-footer.component';

@Component({
  selector: 'app-event-view',
  imports: [CommonModule, TranslocoModule, ModalComponent, PageHeaderComponent, ReactiveFormsModule, SvgWrapperComponent, PageFooterComponent],
  templateUrl: './event-view.component.html',
  styleUrl: './event-view.component.scss'
})
export class EventViewComponent {
  eventId = signal<number | null>(null);

  user = toSignal(this.userService.getUserInfo$());

  event = rxResource({
    params: () => ({ id: this.eventId() }),
    stream: ({ params }) => {
      if (!params.id) return of(null);

      return this.eventsService.getEvent$(params.id).pipe(
        catchError((error) => {
          // Handle errors for event loading (e.g., 404 not found, 401 unauthorized)
          console.warn('Failed to load event:', error.status, error.message);
          return of(null);
        })
      );
    }
  });
  description = computed(() => {
    let descParts = [];

    if (this.event.value().description) {
      descParts = this.event.value().description.trim().split('\n');
    }

    return descParts;
  });

  participants = rxResource({
    params: () => ({ event: this.event.value() }),
    stream: ({ params }) => {
      if (!params.event?.id) return of([]);
      return this.eventsService.getParticipants$(params.event.id, params.event.eventAccessToken);
    }
  });

  participantsInProgress = rxResource({
    params: () => ({ event: this.event.value() }),
    stream: ({ params }) => {
      if (!params.event?.id || !params.event.started) return of([]);
      return this.eventsService.getParticipantsByStatus$(params.event.id, 'p', params.event.eventAccessToken);
    }
  });

  participantsCompleted = rxResource({
    params: () => ({ event: this.event.value() }),
    stream: ({ params }) => {
      if (!params.event?.id || !params.event.started) return of([]);
      return this.eventsService.getParticipantsByStatus$(params.event.id, 'c', params.event.eventAccessToken);
    }
  });

  joinRequests = rxResource({
    params: () => ({ event: this.event.value() }),
    stream: ({ params }) => {
      if (!params.event?.id || !params.event.mine) return of([]);
      return this.eventsService.joinRequests$(params.event.id);
    }
  });

  obtainedRewards = rxResource({
    params: () => ({ event: this.event.value() }),
    stream: ({ params }) => {
      if (!params.event?.id || !params.event.completed) return of(null);
      return this.eventsService.getObtainedRewards$(params.event.id, params.event.eventAccessToken);
    }
  });

  // Milestone summary for admins (leaderboard view)
  milestoneSummary = rxResource({
    params: () => ({ event: this.event.value() }),
    stream: ({ params }) => {
      // Only load milestone summary for admins (mine) and if milestones exist
      if (params.event?.mine && params.event?.config?.compilationMilestones?.length > 0) {
        return this.eventsService.getMilestoneSummary$(params.event.id, params.event.eventAccessToken);
      }
      return of(new Map<string, ParticipantUser[]>());
    }
  });

  // User's compilation progress (for personal milestone view)
  userCompilationProgress = computed(() => {
    const currentUser = this.user();
    const participantsList = this.participants.value() || [];
    const userParticipant = participantsList.find(p => p.id === currentUser?.id);

    if (userParticipant?.milestoneProgress?.length > 0) {
      return userParticipant.milestoneProgress[0]?.currentProgress || 0;
    }
    return 0;
  });

  // Cache for milestone reached dates
  private milestoneReachedDateCache = new Map<string, string>();

  // Computed state using shared event status functions
  isEventStarted = computed(() => isEventStarted(this.event.value()));
  isEventCompleted = computed(() => isEventCompleted(this.event.value()));
  isEventActive = computed(() => isEventActive(this.event.value()));

  isOrganizer = computed(() => {
    const event = this.event.value();
    const currentUser = this.user();
    return event?.organizer === currentUser?.id;
  });

  isAdmin = computed(() => {
    const event = this.event.value();
    return event?.isAdmin || false;
  });

  isMineOrAdmin = computed(() => {
    return this.event.value()?.mine || this.event.value()?.adminsList?.some(admin => admin.userId === this.user()?.id) || false;
  });

  joined = computed(() => {
    const currentUser = this.user();
    const participantsList = this.participants.value() || [];
    return participantsList.some(p => p.id === currentUser?.id);
  });

  numberOfParticipants = computed(() => this.participants.value()?.length || 0);

  // Get organizer info from event data
  organizerInfo = computed(() => {
    return this.event.value()?.organizerDetails;
  });

  // Get current active locale for date formatting
  // Maps language codes to proper locale codes
  currentLocale = computed(() => getLocaleFromLang(this.transloco.getActiveLang()));

  // Header buttons configuration
  headerButtons = computed<PageHeaderButton[]>(() => {
    const buttons: PageHeaderButton[] = [];
    const eventData = this.event.value();

    if (this.isOrganizer()) {
      buttons.push({
        label: 'edit',
        icon: 'project-icons/pencil',
        onClick: () => this.editEvent(),
        type: 'primary'
      });
    }

    // if (eventData?.visibility === 'private' && this.isMineOrAdmin()) {
    //   buttons.push({
    //     label: 'share',
    //     icon: 'project-icons/share',
    //     onClick: () => this.generateShareCode(),
    //     type: 'primary'
    //   });
    // }

    return buttons;
  });

  isFull = computed(() => {
    const event = this.event.value();
    return event && event.slots ? this.numberOfParticipants() >= event.slots : false;
  });

  registrationEnded = computed(() => {
    const event = this.event.value();
    return event?.registrationEndDate ? event.registrationEndDate < new Date() : false;
  });

  expired = computed(() => {
    const event = this.event.value();
    return event?.endDate ? event.endDate < new Date() : false;
  });

  canStart = computed(() => {
    const event = this.event.value();
    if (!event || !this.isMineOrAdmin()) return false;
    if (event.started) return false;
    if (!event.quests || event.quests.length === 0) return false;
    if (event.startDate && event.startDate > new Date()) return false;
    return !this.expired();
  });

  canPlay = computed(() => {
    return this.isEventActive() && this.joined();
  });

  // Modal states
  showConfirmStartModal = signal(false);
  showConfirmCompleteModal = signal(false);
  showConfirmLeaveModal = signal(false);
  showShareModal = signal(false);
  shareCode = signal<string | null>(null);
  showAddAdminModal = signal(false);

  // Admin search
  adminSearchControl = new FormControl('', { nonNullable: true });
  adminSearchResults = signal<User[]>([]);
  isSearchingAdmins = signal(false);

  confirmStartButtons: ModalButton[] = [
    {
      type: 'secondary',
      text: this.transloco.translate('cancel'),
      onClick: () => this.showConfirmStartModal.set(false)
    },
    {
      type: 'primary',
      text: this.transloco.translate('start'),
      onClick: () => this.confirmStart()
    }
  ];

  confirmCompleteButtons: ModalButton[] = [
    {
      type: 'secondary',
      text: this.transloco.translate('cancel'),
      onClick: () => this.showConfirmCompleteModal.set(false)
    },
    {
      type: 'primary',
      text: this.transloco.translate('complete'),
      onClick: () => this.confirmComplete()
    }
  ];

  confirmLeaveButtons: ModalButton[] = [
    {
      type: 'secondary',
      text: this.transloco.translate('cancel'),
      onClick: () => this.showConfirmLeaveModal.set(false)
    },
    {
      type: 'error',
      text: this.transloco.translate('leave'),
      onClick: () => this.confirmLeave()
    }
  ];

  shareModalButtons: ModalButton[] = [
    {
      type: 'primary',
      text: this.transloco.translate('close'),
      onClick: () => this.showShareModal.set(false)
    }
  ];

  addAdminModalButtons: ModalButton[] = [
    {
      type: 'secondary',
      text: this.transloco.translate('close'),
      onClick: () => this.closeAddAdminModal()
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventsService: EventsService,
    private userService: UserService,
    private transloco: TranslocoService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.route.params.subscribe(params => {
      const id = parseInt(params['id']);
      if (!isNaN(id)) {
        this.eventId.set(id);
      }
    });

    // Admin search functionality
    this.adminSearchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(searchTerm => {
        if (searchTerm && searchTerm.length >= 2) {
          this.isSearchingAdmins.set(true);
          return this.userService.findUser$(searchTerm, true);
        }
        this.adminSearchResults.set([]);
        return of(null);
      })
    ).subscribe({
      next: (user) => {
        this.isSearchingAdmins.set(false);
        if (user) {
          this.adminSearchResults.set([user]);
        }
      },
      error: () => {
        this.isSearchingAdmins.set(false);
        this.adminSearchResults.set([]);
      }
    });
  }

  // Event Actions
  goBack(): void {
    this.router.navigate(['/events']);
  }

  editEvent(): void {
    const event = this.event.value();
    if (event?.id) {
      this.router.navigate(['/edit-event', event.id]);
    }
  }

  start(): void {
    this.showConfirmStartModal.set(true);
  }

  confirmStart(): void {
    const event = this.event.value();
    if (!event?.id) return;

    this.eventsService.startEvent$(event.id).subscribe({
      next: () => {
        this.showConfirmStartModal.set(false);
        this.snackBar.open(
          this.transloco.translate('eventStartedSuccessfully'),
          'OK',
          { duration: 3000 }
        );
        this.reloadData();
      },
      error: (error) => {
        console.error('Error starting event:', error);
        this.snackBar.open(
          this.transloco.translate('failedToStartEvent'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  complete(): void {
    this.showConfirmCompleteModal.set(true);
  }

  confirmComplete(): void {
    const event = this.event.value();
    if (!event?.id) return;

    this.eventsService.completeEvent$(event.id).subscribe({
      next: () => {
        this.showConfirmCompleteModal.set(false);
        this.snackBar.open(
          this.transloco.translate('eventCompletedSuccessfully'),
          'OK',
          { duration: 3000 }
        );
        this.reloadData();
      },
      error: (error) => {
        console.error('Error completing event:', error);
        this.snackBar.open(
          this.transloco.translate('failedToCompleteEvent'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  // Participant Actions
  join(): void {
    const event = this.event.value();
    if (!event?.id) return;

    this.eventsService.joinEvent$(event.id).subscribe({
      next: () => {
        this.snackBar.open(
          this.transloco.translate('successfullyJoinedEvent'),
          'OK',
          { duration: 3000 }
        );
        this.reloadData();
      },
      error: (error) => {
        console.error('Error joining event:', error);
        this.snackBar.open(
          this.transloco.translate('failedToJoinEvent'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  requestJoin(): void {
    const event = this.event.value();
    if (!event?.id) return;

    this.eventsService.requestToJoinEvent$(event).subscribe({
      next: () => {
        this.snackBar.open(
          this.transloco.translate('joinRequestSent'),
          'OK',
          { duration: 3000 }
        );
      },
      error: (error) => {
        console.error('Error requesting to join:', error);
        this.snackBar.open(
          this.transloco.translate('failedToSendJoinRequest'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  leave(): void {
    this.showConfirmLeaveModal.set(true);
  }

  confirmLeave(): void {
    const event = this.event.value();
    if (!event?.id) return;

    this.eventsService.leaveEvent$(event.id).subscribe({
      next: () => {
        this.showConfirmLeaveModal.set(false);
        this.snackBar.open(
          this.transloco.translate('leftEventSuccessfully'),
          'OK',
          { duration: 3000 }
        );
        this.reloadData();
      },
      error: (error) => {
        console.error('Error leaving event:', error);
        this.snackBar.open(
          this.transloco.translate('failedToLeaveEvent'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  removeUser(userId: number): void {
    const event = this.event.value();
    if (!event) return;

    this.eventsService.removeUserFromEvent$(event, userId).subscribe({
      next: () => {
        this.snackBar.open(
          this.transloco.translate('userRemovedSuccessfully'),
          'OK',
          { duration: 3000 }
        );
        this.reloadData();
      },
      error: (error) => {
        console.error('Error removing user:', error);
        this.snackBar.open(
          this.transloco.translate('failedToRemoveUser'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  acceptJoinRequest(user: any): void {
    const event = this.event.value();
    if (!event?.id) return;

    this.eventsService.acceptJoinRequest$(event.id, user.id).subscribe({
      next: () => {
        this.snackBar.open(
          this.transloco.translate('joinRequestAccepted'),
          'OK',
          { duration: 3000 }
        );
        this.reloadData();
      },
      error: (error) => {
        console.error('Error accepting request:', error);
        this.snackBar.open(
          this.transloco.translate('failedToAcceptRequest'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  rejectJoinRequest(user: any): void {
    const event = this.event.value();
    if (!event?.id) return;

    this.eventsService.rejectJoinRequest$(event.id, user.id).subscribe({
      next: () => {
        this.snackBar.open(
          this.transloco.translate('joinRequestRejected'),
          'OK',
          { duration: 3000 }
        );
        this.reloadData();
      },
      error: (error) => {
        console.error('Error rejecting request:', error);
        this.snackBar.open(
          this.transloco.translate('failedToRejectRequest'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  // Admin Management
  openAddAdminModal(): void {
    this.showAddAdminModal.set(true);
    this.adminSearchControl.setValue('');
    this.adminSearchResults.set([]);
  }

  closeAddAdminModal(): void {
    this.showAddAdminModal.set(false);
    this.adminSearchControl.setValue('');
    this.adminSearchResults.set([]);
  }

  addAdmin(user: User): void {
    const event = this.event.value();
    if (!event?.id) return;

    // Check if user is already an admin
    if (event.adminsList?.some(admin => admin.userId === user.id)) {
      this.snackBar.open(
        this.transloco.translate('userAlreadyAdmin'),
        'OK',
        { duration: 3000 }
      );
      return;
    }

    this.eventsService.addAdmin$(event.id, user.id).subscribe({
      next: () => {
        this.snackBar.open(
          this.transloco.translate('adminAddedSuccessfully'),
          'OK',
          { duration: 3000 }
        );
        this.closeAddAdminModal();
        this.reloadData();
      },
      error: (error) => {
        console.error('Error adding admin:', error);
        this.snackBar.open(
          this.transloco.translate('failedToAddAdmin'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  removeAdmin(admin: EventAdmin): void {
    const event = this.event.value();
    if (!event?.id) return;

    this.eventsService.removeAdmin$(event.id, admin.id).subscribe({
      next: () => {
        this.snackBar.open(
          this.transloco.translate('adminRemovedSuccessfully'),
          'OK',
          { duration: 3000 }
        );
        this.reloadData();
      },
      error: (error) => {
        console.error('Error removing admin:', error);
        this.snackBar.open(
          this.transloco.translate('failedToRemoveAdmin'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  // Sharing
  // this is not fully implemented yet
  generateShareCode(): void {
    const event = this.event.value();
    if (!event?.id) return;

    this.eventsService.generateBatchAccessCode$(event.id).subscribe({
      next: (response) => {
        this.shareCode.set(response.code);
        this.showShareModal.set(true);
      },
      error: (error) => {
        console.error('Error generating share code:', error);
        this.snackBar.open(
          this.transloco.translate('failedToGenerateShareCode'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  // Utilities
  reloadData(): void {
    this.event.reload();
    this.participants.reload();
    this.participantsInProgress.reload();
    this.participantsCompleted.reload();
    this.joinRequests.reload();
    this.obtainedRewards.reload();
    this.milestoneSummary.reload();
  }

  /**
   * Opens a quest in a new tab
   */
  openQuestInNewTab(questId: number): void {
    window.open(`/quest/${questId}`, '_blank');
  }

  /**
   * Opens an obelisk in a new tab
   */
  openObeliskInNewTab(obeliskUuid: string): void {
    window.open(`/obelisk/${obeliskUuid}`, '_blank');
  }

  /**
   * Opens a trip in a new tab
   */
  openTripInNewTab(tripId: number): void {
    window.open(`/trip/${tripId}`, '_blank');
  }

  /**
   * Gets the translation key for event visibility value
   */
  getVisibilityTranslationKey(visibility: string): string {
    // Visibility values are already in camelCase: public, contacts, private
    return visibility;
  }

  /**
   * Gets the translation key for event access value
   */
  getAccessTranslationKey(access: string): string {
    // Map access values to translation keys
    const accessMap: { [key: string]: string } = {
      'free': 'free',
      'approval': 'approvalRequired',
      'paid': 'paid',
      'approval_paid': 'approvalRequired' // This might need a different key if there's a specific translation
    };
    return accessMap[access] || access;
  }

  /**
   * Gets the first paragraph of a trip description (split by \n)
   */
  getTripDescriptionPreview(description: string): string {
    if (!description) return '';
    const firstParagraph = description.split('\n')[0];
    // Limit to 200 characters if too long
    return firstParagraph.length > 200
      ? firstParagraph.substring(0, 200) + '...'
      : firstParagraph;
  }

  /**
   * Formats a date using the current locale
   */
  formatEventDate(date: Date | undefined): string {
    return formatDate(date, this.currentLocale());
  }

  /**
   * Gets formatted reached date for a milestone from a participant's milestone progress.
   * Uses memoization to avoid recalculating on each change detection cycle.
   */
  getMilestoneReachedDate(participant: ParticipantUser, milestoneName: string): string | undefined {
    if (!participant?.milestoneProgress) return undefined;

    const cacheKey = `${participant.id}-${milestoneName}`;

    // Check if we already have it cached
    if (this.milestoneReachedDateCache.has(cacheKey)) {
      return this.milestoneReachedDateCache.get(cacheKey);
    }

    // Find the milestone and format the date
    const milestone = participant.milestoneProgress.find(m => m.name === milestoneName || m.percentage.toString() === milestoneName);
    const formattedDate = milestone?.reachedAt ? formatDate(milestone.reachedAt, this.currentLocale()) : undefined;

    // Cache the result if it exists
    if (formattedDate) {
      this.milestoneReachedDateCache.set(cacheKey, formattedDate);
    }

    return formattedDate;
  }

  /**
   * Gets participants who reached a specific milestone
   */
  getMilestoneParticipants(milestone: { percentage: number; name?: string }): ParticipantUser[] {
    const key = milestone.name || milestone.percentage.toString();
    return this.milestoneSummary.value()?.get(key) || [];
  }

  /**
   * Gets count of participants who reached a specific milestone
   */
  getMilestoneCount(milestone: { percentage: number; name?: string }): number {
    return this.getMilestoneParticipants(milestone).length;
  }

  /**
   * Checks if user has reached a milestone based on their progress
   */
  hasReachedMilestone(milestone: { percentage: number }): boolean {
    return this.userCompilationProgress() >= milestone.percentage;
  }

  EventAccess = EventAccess;
}

